import { useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { DataTable, getSelectColumn, SortableHeader } from '@/components/ui/data-table';
import { StatusBadge } from '@/components/ui/status-badge';
import { FloatingActionBar } from '@/components/ui/floating-action-bar';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
  ListPageActions,
  ListPageCard,
  ListPageFilters,
  ListPageStats,
  ListPageContent,
} from '@/components/ui/list-page';
import { useApp } from '@/components/AppLayout';
import { quoteApi, dashboardApi, clientApi, queryFn, type Quote } from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { MoreVertical, FileEdit, CalendarIcon, Users, Send, X, Archive, Trash, ChevronDown, ListFilter } from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, subMonths, subDays } from 'date-fns';
import { toast } from 'sonner';

type DateRangePreset = 'all' | 'last_7' | 'last_30' | 'last_month' | 'this_month' | 'last_12' | 'ytd' | 'custom';

type StatusFilter = 'all' | 'draft' | 'awaiting_response' | 'approved' | 'changes_requested' | 'converted';

const PAGE_SIZE = 25;

function QuotesContent() {
  const { nav } = useBusinessNav();
  const { currentBusiness, isViewOnly } = useApp();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPageIndex(0);
  };

  // Server-side pagination state
  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Initialize status filter from URL params
  const urlStatus = searchParams.get('status') as StatusFilter | null;
  const validStatuses: StatusFilter[] = ['all', 'draft', 'awaiting_response', 'approved', 'changes_requested', 'converted'];
  const initialStatus: StatusFilter = urlStatus && validStatuses.includes(urlStatus) ? urlStatus : 'all';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatus);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<StatusFilter>(initialStatus);
  const [selectedQuotes, setSelectedQuotes] = useState<Quote[]>([]);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('all');
  const [customDateRange, setCustomDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  // Pending state for staged selections before Apply
  const [pendingPreset, setPendingPreset] = useState<DateRangePreset>('all');
  const [pendingCustomRange, setPendingCustomRange] = useState<{ from?: Date; to?: Date }>({});
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);

  // Client filter state
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [pendingClientIds, setPendingClientIds] = useState<string[]>([]);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients', currentBusiness?.id],
    queryFn: () => queryFn(clientApi.list(currentBusiness!.id)),
    enabled: !!currentBusiness,
  });

  const getDateRangeParams = () => {
    const today = new Date();
    let startDate: string | undefined;
    let endDate: string | undefined;

    switch (dateRangePreset) {
      case 'last_7':
        startDate = format(subDays(today, 7), 'yyyy-MM-dd');
        break;
      case 'last_30':
        startDate = format(subDays(today, 30), 'yyyy-MM-dd');
        break;
      case 'last_month':
        const lastMonth = subMonths(today, 1);
        startDate = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
        endDate = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
        break;
      case 'this_month':
        startDate = format(startOfMonth(today), 'yyyy-MM-dd');
        endDate = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'last_12':
        startDate = format(subMonths(today, 12), 'yyyy-MM-dd');
        break;
      case 'ytd':
        startDate = format(startOfYear(today), 'yyyy-MM-dd');
        break;
      case 'custom':
        if (customDateRange.from) {
          startDate = format(customDateRange.from, 'yyyy-MM-dd');
        }
        if (customDateRange.to) {
          endDate = format(customDateRange.to, 'yyyy-MM-dd');
        }
        break;
    }

    return { startDate, endDate };
  };

  const getOrderingParam = () => {
    if (sorting.length === 0) return undefined;
    const { id, desc } = sorting[0];
    return desc ? `-${id}` : id;
  };

  const { startDate, endDate } = getDateRangeParams();
  const statusQuery = statusFilter !== 'all' ? statusFilter : undefined;
  const clientIdsToFilter = selectedClientIds.length > 0 ? selectedClientIds : undefined;

  const { data: quotesPage, isLoading } = useQuery({
    queryKey: ['quotes', currentBusiness?.id, clientIdsToFilter, statusQuery, startDate, endDate, pageIndex, sorting, searchQuery],
    queryFn: () => queryFn(quoteApi.list(
      currentBusiness!.id,
      clientIdsToFilter,
      statusQuery,
      startDate,
      endDate,
      {
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
        ordering: getOrderingParam(),
      },
      searchQuery || undefined
    )),
    enabled: !!currentBusiness,
    placeholderData: keepPreviousData,
  });

  const quotes = quotesPage?.results ?? [];
  const totalCount = quotesPage?.count ?? 0;

  const { data: counts = null } = useQuery({
    queryKey: ['quote-counts', currentBusiness?.id, clientIdsToFilter],
    queryFn: () => queryFn(dashboardApi.getQuoteCounts(currentBusiness!.id, clientIdsToFilter)),
    enabled: !!currentBusiness,
  });

  const invalidateQuotes = () => {
    queryClient.invalidateQueries({ queryKey: ['quotes'] });
    queryClient.invalidateQueries({ queryKey: ['quote-counts'] });
  };

  const handleDeleteQuote = async (id: string) => {
    if (!confirm('Are you sure you want to delete this quote?')) return;
    const response = await quoteApi.delete(id);
    if (response.error) {
      alert(response.error);
    } else {
      invalidateQuotes();
    }
  };

  // Bulk action handlers
  const handleBulkSend = async () => {
    // Filter to only sendable quotes (draft or awaiting_response)
    const sendableQuotes = selectedQuotes.filter(
      q => q.status === 'draft' || q.status === 'awaiting_response'
    );

    if (sendableQuotes.length === 0) {
      toast.error('No quotes can be sent');
      return;
    }

    let sentCount = 0;
    let reminderCount = 0;

    for (const quote of sendableQuotes) {
      const client = clients.find(c => c.id === quote.client);
      const result = await quoteApi.send(quote.id, {
        medium: 'email',
        destination: client?.primary_email || '',
        message: '',
        cc_self: false
      });

      if (!result.error) {
        if (quote.status === 'draft') {
          sentCount++;
        } else {
          reminderCount++;
        }
      }
    }

    // Show toast notification
    const parts: string[] = [];
    if (sentCount > 0) {
      parts.push(`${sentCount} quote${sentCount !== 1 ? 's' : ''} sent`);
    }
    if (reminderCount > 0) {
      parts.push(`${reminderCount} reminder${reminderCount !== 1 ? 's' : ''} sent`);
    }

    if (parts.length > 0) {
      toast.success(parts.join(', '));
    }

    setSelectedQuotes([]);
    invalidateQuotes();
  };

  const handleBulkArchive = async () => {
    for (const quote of selectedQuotes) {
      await quoteApi.archive(quote.id);
    }
    setSelectedQuotes([]);
    invalidateQuotes();
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedQuotes.length} quote(s)?`)) return;
    for (const quote of selectedQuotes) {
      await quoteApi.delete(quote.id);
    }
    setSelectedQuotes([]);
    invalidateQuotes();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };


  const columns: ColumnDef<Quote>[] = [
    getSelectColumn<Quote>(),
    {
      accessorKey: 'quote_number',
      header: ({ column }) => <SortableHeader column={column} title="Quote #" />,
      cell: ({ row }) => (
        <span className="text-sm font-medium">{row.getValue('quote_number')}</span>
      ),
    },
    {
      accessorKey: 'client_name',
      header: ({ column }) => <SortableHeader column={column} title="Client" />,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{row.original.client_name || '-'}</div>
          {row.original.client_company && (
            <div className="text-xs text-muted-foreground truncate">{row.original.client_company}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'quote_date',
      header: ({ column }) => <SortableHeader column={column} title="Date" />,
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDate(row.getValue('quote_date'))}
        </span>
      ),
    },
    {
      accessorKey: 'due_date',
      header: ({ column }) => <SortableHeader column={column} title="Due Date" />,
      cell: ({ row }) => (
        <span className="text-sm">
          {formatDate(row.getValue('due_date'))}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string;

        const config: Record<string, { dot: 'gray' | 'blue' | 'green' | 'orange' | 'purple'; label: string }> = {
          draft: { dot: 'gray', label: 'Draft' },
          awaiting_response: { dot: 'blue', label: 'Awaiting Response' },
          approved: { dot: 'green', label: 'Approved' },
          changes_requested: { dot: 'orange', label: 'Changes Requested' },
          converted: { dot: 'purple', label: 'Converted' },
        };

        const statusConfig = config[status] || { dot: 'gray', label: status };
        return <StatusBadge dot={statusConfig.dot} label={statusConfig.label} />;
      },
    },
    {
      accessorKey: 'total_amount',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader column={column} title="Total" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right text-sm font-medium">
          {formatAmount(row.getValue('total_amount'))}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteQuote(row.original.id);
                }}
                className="text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const hasFilters = statusFilter !== 'all' || dateRangePreset !== 'all' || selectedClientIds.length > 0 || searchQuery !== '';

  // Calculate outstanding count (awaiting response)
  const outstandingCount = counts ? counts.awaiting_response : 0;

  return (
    <>
      <ListPage>
        <ListPageHeader>
          <ListPageTitle>Quotes</ListPageTitle>
          <ListPageActions>
            {!isViewOnly && (
              <Button onClick={() => nav('/quotes/create')} className="rounded-full">
                <Send className="h-4 w-4" />
                New Quote
              </Button>
            )}
          </ListPageActions>
        </ListPageHeader>

        <ListPageCard>
          <ListPageFilters>
          <Input
            placeholder="Search quotes..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-[280px] bg-white"
          />
          <Popover
            open={statusPopoverOpen}
            onOpenChange={(open) => {
              setStatusPopoverOpen(open);
              if (open) setPendingStatus(statusFilter);
            }}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-auto pr-6 justify-start text-left font-normal rounded-full">
                <ListFilter className="h-4 w-4" />
                {statusFilter === 'all' ? `All ${counts ? `(${counts.total})` : ''}` :
                 statusFilter === 'draft' ? `Draft ${counts ? `(${counts.draft})` : ''}` :
                 statusFilter === 'awaiting_response' ? `Awaiting Response ${counts ? `(${counts.awaiting_response})` : ''}` :
                 statusFilter === 'approved' ? `Approved ${counts ? `(${counts.approved})` : ''}` :
                 statusFilter === 'changes_requested' ? `Changes Requested ${counts ? `(${counts.changes_requested})` : ''}` :
                 `Converted ${counts ? `(${counts.converted})` : ''}`}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <div className="p-2 space-y-1">
                {[
                  { value: 'all', label: 'All', count: counts?.total },
                  { value: 'draft', label: 'Draft', count: counts?.draft },
                  { value: 'awaiting_response', label: 'Awaiting Response', count: counts?.awaiting_response },
                  { value: 'approved', label: 'Approved', count: counts?.approved },
                  { value: 'changes_requested', label: 'Changes Requested', count: counts?.changes_requested },
                  { value: 'converted', label: 'Converted', count: counts?.converted },
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant={pendingStatus === option.value ? 'secondary' : 'ghost'}
                    className="w-full justify-start gap-2"
                    onClick={() => setPendingStatus(option.value as StatusFilter)}
                  >
                    <span className={`h-2 w-2 rounded-full ${pendingStatus === option.value ? 'bg-primary' : 'bg-transparent'}`} />
                    {option.label} {option.count !== undefined && `(${option.count})`}
                  </Button>
                ))}
                <div className="border-t mt-2 pt-2">
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setStatusFilter(pendingStatus);
                      setPageIndex(0);
                      if (pendingStatus === 'all') {
                        searchParams.delete('status');
                      } else {
                        searchParams.set('status', pendingStatus);
                      }
                      setSearchParams(searchParams, { replace: true });
                      setStatusPopoverOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Popover
              open={datePopoverOpen}
              onOpenChange={(open) => {
                setDatePopoverOpen(open);
                if (open) {
                  // Sync pending state with current state when opening
                  setPendingPreset(dateRangePreset);
                  setPendingCustomRange(customDateRange);
                  setShowCalendarPicker(false); // Always show options first
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-auto pr-6 justify-start text-left font-normal rounded-full">
                  <CalendarIcon className="h-4 w-4" />
                  {dateRangePreset === 'all' && 'All Time'}
                  {dateRangePreset === 'last_7' && 'Last 7 Days'}
                  {dateRangePreset === 'last_30' && 'Last 30 Days'}
                  {dateRangePreset === 'last_month' && 'Last Month'}
                  {dateRangePreset === 'this_month' && 'This Month'}
                  {dateRangePreset === 'last_12' && 'Last 12 Months'}
                  {dateRangePreset === 'ytd' && 'Year to Date'}
                  {dateRangePreset === 'custom' && (
                    customDateRange.from ? (
                      customDateRange.to ? (
                        `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d, yyyy')}`
                      ) : (
                        format(customDateRange.from, 'MMM d, yyyy')
                      )
                    ) : (
                      'Custom Range'
                    )
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className={showCalendarPicker ? "w-auto p-0" : "w-[220px] p-0"} align="start">
                {!showCalendarPicker ? (
                  <div className="p-2 space-y-1">
                    {[
                      { value: 'all', label: 'All Time' },
                      { value: 'last_7', label: 'Last 7 Days' },
                      { value: 'last_30', label: 'Last 30 Days' },
                      { value: 'last_month', label: 'Last Month' },
                      { value: 'this_month', label: 'This Month' },
                      { value: 'last_12', label: 'Last 12 Months' },
                      { value: 'ytd', label: 'Year to Date' },
                    ].map((option) => (
                      <Button
                        key={option.value}
                        variant={pendingPreset === option.value ? 'secondary' : 'ghost'}
                        className="w-full justify-start gap-2"
                        onClick={() => setPendingPreset(option.value as DateRangePreset)}
                      >
                        <span className={`h-2 w-2 rounded-full ${pendingPreset === option.value ? 'bg-primary' : 'bg-transparent'}`} />
                        {option.label}
                      </Button>
                    ))}
                    <Button
                      variant={pendingPreset === 'custom' ? 'secondary' : 'ghost'}
                      className="w-full justify-start gap-2"
                      onClick={() => setShowCalendarPicker(true)}
                    >
                      <span className={`h-2 w-2 rounded-full ${pendingPreset === 'custom' ? 'bg-primary' : 'bg-transparent'}`} />
                      Custom Range
                    </Button>
                    <div className="border-t mt-2 pt-2">
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setDateRangePreset(pendingPreset);
                          setPageIndex(0); // Reset to first page when filter changes
                          setDatePopoverOpen(false);
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-3">
                    <Calendar
                      mode="range"
                      selected={{ from: pendingCustomRange.from, to: pendingCustomRange.to }}
                      onSelect={(range) => setPendingCustomRange({ from: range?.from, to: range?.to })}
                      numberOfMonths={2}
                    />
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          setShowCalendarPicker(false);
                          setPendingCustomRange({});
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          setDateRangePreset('custom');
                          setCustomDateRange(pendingCustomRange);
                          setPageIndex(0); // Reset to first page when filter changes
                          setDatePopoverOpen(false);
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>

          {/* Client Filter */}
          <Popover
            open={clientPopoverOpen}
            onOpenChange={(open) => {
              setClientPopoverOpen(open);
              if (open) {
                setPendingClientIds(selectedClientIds);
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start text-left font-normal gap-2 rounded-full">
                <Users className="h-4 w-4" />
                {selectedClientIds.length === 0 ? (
                  'All Clients'
                ) : selectedClientIds.length === 1 ? (
                  clients.find(c => c.id === selectedClientIds[0])?.billing_name || '1 client'
                ) : (
                  `${selectedClientIds.length} clients`
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search clients..." />
                <CommandList>
                  <CommandEmpty>No clients found.</CommandEmpty>
                  <CommandGroup>
                    {clients.map((client) => (
                      <CommandItem
                        key={client.id}
                        onSelect={() => {
                          setPendingClientIds(prev =>
                            prev.includes(client.id)
                              ? prev.filter(id => id !== client.id)
                              : [...prev, client.id]
                          );
                        }}
                      >
                        <Checkbox
                          checked={pendingClientIds.includes(client.id)}
                          className="mr-2"
                        />
                        <div className="flex flex-col">
                          <span>{client.billing_name}</span>
                          {client.company_name && (
                            <span className="text-xs text-muted-foreground">{client.company_name}</span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
                <div className="border-t p-2 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setPendingClientIds([]);
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setSelectedClientIds(pendingClientIds);
                      setPageIndex(0); // Reset to first page when filter changes
                      setClientPopoverOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </Command>
            </PopoverContent>
          </Popover>

          {(statusFilter !== 'all' || dateRangePreset !== 'all' || selectedClientIds.length > 0) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter('all');
                setDateRangePreset('all');
                setCustomDateRange({});
                setSelectedClientIds([]);
                setPageIndex(0); // Reset to first page when filters cleared
              }}
              className="text-muted-foreground rounded-full"
            >
              <X className="h-4 w-4" />
              Clear all filters
            </Button>
          )}
          </ListPageFilters>

          <ListPageStats>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Pending Invoicing</div>
              <div className="text-2xl font-medium text-green-600">
                {counts?.approved ?? '-'}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Accepted this month</div>
              <div className="text-2xl font-medium">
                {counts?.approved_this_month ?? '-'}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">Outstanding</div>
              <div className="text-2xl font-medium">
                {outstandingCount}
              </div>
            </div>
          </ListPageStats>

          <ListPageContent>
            <DataTable
              columns={columns}
              data={quotes}
              loading={isLoading}
              onRowClick={(quote) => nav(`/quotes/${quote.id}`)}
              noBorder
              enableRowSelection
              onSelectionChange={setSelectedQuotes}
              emptyIcon={<FileEdit className="h-6 w-6 text-muted-foreground" />}
              emptyTitle={hasFilters ? "No quotes found" : "No quotes yet"}
              emptyDescription={hasFilters ? "Try adjusting your search or filters." : "Create your first quote to get started."}
              // Server-side pagination
              manualPagination
              manualSorting
              pageSize={PAGE_SIZE}
              pageIndex={pageIndex}
              totalCount={totalCount}
              onPaginationChange={(newPageIndex) => setPageIndex(newPageIndex)}
              sorting={sorting}
              onSortingChange={(newSorting) => {
                setSorting(newSorting);
                setPageIndex(0); // Reset to first page when sort changes
              }}
            />
          </ListPageContent>
        </ListPageCard>
      </ListPage>

      <FloatingActionBar
        selectedCount={selectedQuotes.length}
        onClear={() => setSelectedQuotes([])}
      >
        <Button
          size="sm"
          variant="ghost"
          className="text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20"
          onClick={handleBulkSend}
        >
          <Send className="h-4 w-4 mr-1" /> Send
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20"
          onClick={handleBulkArchive}
        >
          <Archive className="h-4 w-4 mr-1" /> Archive
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/20"
          onClick={handleBulkDelete}
        >
          <Trash className="h-4 w-4 mr-1" /> Delete
        </Button>
      </FloatingActionBar>
    </>
  );
}

export function Quotes() {
  return <QuotesContent />;
}
